import { useCallback, useState } from 'react';
import { useAlert } from '@weave/alert-system';
import { getParsedErrorMessage } from 'redux/axios';
import { MetricResult } from '../../handoff-metrics/handoff-metrics.types';
import {
  UnifyLocationDetails,
  UnifyLocationRow,
} from '../components/unify-locations-table/unify-locations-table.types';
import {
  buildBatchCreatePayload,
  buildBulkUpdatePayload,
  useBatchCreateHandoffSnapshotsByLocationIDsMutation,
  useBatchSubmitHandoffRecordsMutation,
  useBulkUpdateHandoffRecordsMutation,
} from '../queries/unify-handoff.queries';

export type UnifyWizardStep = 'select-locations' | 'evaluation' | 'snapshot';

export interface UseUnifyHandoffSubmitProps {
  currentStep: UnifyWizardStep;
  setCurrentStep: React.Dispatch<React.SetStateAction<UnifyWizardStep>>;
  selectedLocations: UnifyLocationRow[];
  locationDetailsMap: Record<string, UnifyLocationDetails>;
  groupId?: string;
  setGroupId: React.Dispatch<React.SetStateAction<string | undefined>>;
  handoffIdMap: Record<string, string>;
  setHandoffIdMap: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  canProceedToEvaluation: boolean;
  setShowValidationErrors: React.Dispatch<React.SetStateAction<boolean>>;
  isSavingProp?: boolean;
  isSubmittingProp?: boolean;
  onSaveAndClose?: (notes?: Record<string, UnifyLocationDetails>) => void;
  onSubmit?: (resultsByLocation: Record<string, MetricResult[]>) => void;
}

export interface UseUnifyHandoffSubmitReturnValues {
  isSaving: boolean;
  isSubmitting: boolean;
  saveOrUpdateDraft: () => Promise<void>;
  handleSaveAndClose: () => Promise<void>;
  goToNextStep: () => Promise<boolean>;
  handleSubmit: (resultsByLocation: Record<string, MetricResult[]>) => Promise<void>;
}

export const useUnifyHandoffSubmit = ({
  currentStep,
  setCurrentStep,
  selectedLocations,
  locationDetailsMap,
  groupId,
  setGroupId,
  handoffIdMap,
  setHandoffIdMap,
  canProceedToEvaluation,
  setShowValidationErrors,
  isSavingProp = false,
  isSubmittingProp = false,
  onSaveAndClose = () => {},
  onSubmit = () => {},
}: UseUnifyHandoffSubmitProps): UseUnifyHandoffSubmitReturnValues => {
  const alert = useAlert();

  const [isSubmittingBatch, setIsSubmittingBatch] = useState<boolean>(false);

  const { mutateAsync: batchCreateSnapshots, isLoading: isBatchCreating } =
    useBatchCreateHandoffSnapshotsByLocationIDsMutation();

  const { mutateAsync: bulkUpdateSnapshots, isLoading: isBulkUpdating } =
    useBulkUpdateHandoffRecordsMutation();

  const { mutateAsync: batchSubmitSnapshots, isLoading: isBatchSubmitting } =
    useBatchSubmitHandoffRecordsMutation();

  const isSaving = isSavingProp || isBatchCreating || isBulkUpdating;
  const isSubmitting = isSubmittingProp || isBatchSubmitting || isSubmittingBatch;

  const saveOrUpdateDraft = useCallback(async () => {
    if (!groupId) {
      const handoffs = buildBatchCreatePayload(selectedLocations, locationDetailsMap);
      const response = await batchCreateSnapshots({ handoffs });
      if (response?.groupId) {
        setGroupId(response.groupId);
      }
      if (response?.handoffResponses) {
        const newIdMap: Record<string, string> = {};
        response.handoffResponses.forEach((res) => {
          if (res.locationId && res.handoffId) {
            newIdMap[res.locationId] = res.handoffId;
          }
        });
        setHandoffIdMap((prev) => ({ ...prev, ...newIdMap }));
      }
    } else {
      const entries = buildBulkUpdatePayload(
        selectedLocations,
        locationDetailsMap,
        groupId,
        handoffIdMap
      );
      await bulkUpdateSnapshots({ entries });
    }
  }, [
    groupId,
    handoffIdMap,
    selectedLocations,
    locationDetailsMap,
    batchCreateSnapshots,
    bulkUpdateSnapshots,
    setGroupId,
    setHandoffIdMap,
  ]);

  const handleSaveAndClose = useCallback(async () => {
    setShowValidationErrors(true);
    if (currentStep === 'select-locations' && !canProceedToEvaluation) {
      return;
    }

    try {
      await saveOrUpdateDraft();
      onSaveAndClose(locationDetailsMap);
    } catch (err) {
      alert.error(getParsedErrorMessage(err) || 'Error saving handoff drafts');
      console.error('Failed to save and close unify handoff draft:', err);
    }
  }, [
    currentStep,
    canProceedToEvaluation,
    setShowValidationErrors,
    saveOrUpdateDraft,
    onSaveAndClose,
    locationDetailsMap,
    alert,
  ]);

  const goToNextStep = useCallback(async () => {
    setShowValidationErrors(true);
    if (!canProceedToEvaluation) {
      return false;
    }

    try {
      await saveOrUpdateDraft();
      setCurrentStep('evaluation');
      return true;
    } catch (err) {
      alert.error(getParsedErrorMessage(err) || 'Error saving handoff drafts');
      console.error('Failed to create/update unify handoff draft on next:', err);
      return false;
    }
  }, [
    canProceedToEvaluation,
    setShowValidationErrors,
    saveOrUpdateDraft,
    setCurrentStep,
    alert,
  ]);

  const handleSubmit = useCallback(
    async (resultsByLocation: Record<string, MetricResult[]>) => {
      setIsSubmittingBatch(true);
      try {
        let activeGroupId = groupId;
        let activeHandoffIdMap = { ...handoffIdMap };

        // 1. Ensure drafts exist with snapshot IDs before submitting
        const hasMissingSnapshots = selectedLocations.some(
          (loc) => !activeHandoffIdMap[loc.locationId]
        );

        if (!activeGroupId || hasMissingSnapshots) {
          const handoffs = buildBatchCreatePayload(selectedLocations, locationDetailsMap);
          const createResponse = await batchCreateSnapshots({ handoffs });
          if (createResponse?.groupId) {
            activeGroupId = createResponse.groupId;
            setGroupId(createResponse.groupId);
          }
          if (createResponse?.handoffResponses) {
            const newIdMap: Record<string, string> = {};
            createResponse.handoffResponses.forEach((res) => {
              if (res.locationId && res.handoffId) {
                newIdMap[res.locationId] = res.handoffId;
              }
            });
            activeHandoffIdMap = { ...activeHandoffIdMap, ...newIdMap };
            setHandoffIdMap((prev) => ({ ...prev, ...newIdMap }));
          }
        }

        // 2. Persist metrics and notes via BulkUpdateHandoffRecords
        const updateEntries = buildBulkUpdatePayload(
          selectedLocations,
          locationDetailsMap,
          activeGroupId,
          activeHandoffIdMap,
          resultsByLocation
        );
        await bulkUpdateSnapshots({ entries: updateEntries });

        // 3. Submit batch via BatchSubmitHandoffRecords
        const submissions = selectedLocations.map((loc) => ({
          locationId: loc.locationId,
          handoffId: activeHandoffIdMap[loc.locationId] || '',
        }));

        await batchSubmitSnapshots({
          submissions,
        });

        // 4. Toast alert
        alert.success('Handoff Hub batch submitted successfully');

        // 5. Invoke onSubmit callback
        await onSubmit(resultsByLocation);
      } catch (err) {
        alert.error(getParsedErrorMessage(err) || 'Error submitting handoff batch');
        console.error('Failed to submit unify handoff batch:', err);
      } finally {
        setIsSubmittingBatch(false);
      }
    },
    [
      groupId,
      handoffIdMap,
      selectedLocations,
      locationDetailsMap,
      batchCreateSnapshots,
      bulkUpdateSnapshots,
      batchSubmitSnapshots,
      alert,
      onSubmit,
      setGroupId,
      setHandoffIdMap,
    ]
  );

  return {
    isSaving,
    isSubmitting,
    saveOrUpdateDraft,
    handleSaveAndClose,
    goToNextStep,
    handleSubmit,
  };
};
